import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type FederationPeerStatus = 'pending' | 'active' | 'paused' | 'revoked';

export interface FederationPeer {
  id: string;
  display_name: string;
  remote_registry_alias: string;
  remote_issuer_url: string;
  mirror_capability_ids: string[];
  status: FederationPeerStatus;
  last_secret_rotated_at: string | null;
  last_handshake_at: string | null;
  created: string;
  paused_at: string | null;
  revoked_at: string | null;
}

export interface FederationPeersListResponse {
  peers: FederationPeer[];
}

export interface FederationPeerSecretReveal {
  peer: FederationPeer;
  client_id: string;
  client_secret: string;
  issued_at?: string;
  rotated_at?: string;
}

export interface CreateFederationPeerBody {
  display_name: string;
  remote_registry_alias: string;
  remote_issuer_url: string;
  mirror_capability_ids?: string[];
}

const QUERY_KEY = ['admin-federation-peers'] as const;

export function useFederationPeers(enabled = true) {
  return useQuery<FederationPeersListResponse>({
    enabled,
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const r = await api.get('/federation/peers');
      return r.data as FederationPeersListResponse;
    },
  });
}

export function useCreateFederationPeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFederationPeerBody) => {
      const r = await api.post('/federation/peers', body);
      return r.data as FederationPeerSecretReveal;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRotateFederationPeerSecret() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (peerId: string) => {
      const r = await api.post(`/federation/peers/${peerId}/rotate-secret`, {});
      return r.data as FederationPeerSecretReveal;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function usePauseFederationPeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (peerId: string) => {
      const r = await api.post(`/federation/peers/${peerId}/pause`, {});
      return (r.data as { peer: FederationPeer }).peer;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useRevokeFederationPeer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (peerId: string) => {
      const r = await api.post(`/federation/peers/${peerId}/revoke`, {});
      return (r.data as { peer: FederationPeer }).peer;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
